import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  PopoverBody,
  PopoverContent,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displayNameFromProperty } from '../../../domain/friendly-properties';
import {
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import {
  ForgeAttributeType,
  ForgeAttributeTypeInput,
  PropertySetType,
} from '../../../gql/graphql';
import { PropertyCascader } from '../../common/PropertyCascader';
import SparkelIcon from '../../common/icon/SparkelIcon';

type GroupByAttributePopoverProps = {
  propertySets: PropertySetType[];
  isOpen: boolean;
  isUpdatingTable: boolean;
  defaultAttributes: ForgeAttributeType[] | null;
  onGroupBy: (attributes: ForgeAttributeTypeInput[]) => Promise<void>;
  onUngroup: () => Promise<void>;
  onUsingCascader?: (e: boolean) => void;
};

type CascaderData = {
  label: string;
  children?: CascaderData[];
};

export function GroupByAttributePopover({
  propertySets,
  isOpen,
  defaultAttributes,
  isUpdatingTable,
  onGroupBy,
  onUngroup,
  onUsingCascader,
}: GroupByAttributePopoverProps): React.ReactElement {
  const { t } = useTranslation(['common', 'categories', 'table']);
  const onSubmitInternal = (indices: number[], groupingIndex?: number) => {
    let submissionData: { category: string; name: string }[] = [];

    if (groupingProperties) {
      // Build existing grouping data
      submissionData = groupingProperties
        .map((prop) => {
          if (
            prop.propertySetIndex === undefined ||
            prop.propertyIndex === undefined
          ) {
            return undefined;
          }
          let category = propertySets[prop.propertySetIndex].category;
          if (category === t('quantities')) {
            category = SPARKEL_GEOMETRIC_PROPERTY_SET;
          } else if (category === t('location')) {
            category = SPARKEL_LOCATION_PROPERTY_SET;
          }
          return {
            category,
            name: propertySets[prop.propertySetIndex].properties[
              prop.propertyIndex
            ],
          };
        })
        .filter(
          (item): item is { category: string; name: string } =>
            item !== undefined
        );
    }

    if (indices && indices.length == 2) {
      const [categoryIndex, attributeNameIndex] = indices;

      const selectedDisplayCategory = propertySets[categoryIndex].category;
      const selectedAttributeName =
        propertySets[categoryIndex].properties[attributeNameIndex];

      const newGrouping = {
        category: selectedDisplayCategory,
        name: selectedAttributeName,
      };

      if (groupingIndex !== undefined) {
        // Replace existing grouping with new data
        submissionData[groupingIndex] = newGrouping;
      } else {
        // Add new data to grouping
        submissionData.push(newGrouping);
      }

      setIsLoading(true);
      setSubmittingGroupBy(true);
      onGroupBy(submissionData).finally(() => setSubmittingGroupBy(false));
    } else if (groupingIndex !== undefined) {
      // Removing grouping at groupingIndex
      submissionData = submissionData.filter((_, idx) => idx !== groupingIndex);

      setSubmittingUngroup(true);
      setUngroupIndex(groupingIndex);
      setIsLoading(true);
      onGroupBy(submissionData).then(() => setSubmittingUngroup(false));
    }
  };

  const onUngroupInternal = async (index?: number) => {
    if (index !== undefined && groupingProperties?.length !== 1) {
      // Remove specific grouping
      onSubmitInternal([], index);
    } else {
      // Remove all grouping
      setIsLoading(true);
      setUngroupIndex(0);
      try {
        await onUngroup();
      } finally {
        setGroupingProperties(null);
        setSubmittingUngroup(false);
      }
    }
  };

  const [groupingProperties, setGroupingProperties] = useState<
    | {
        propertySetIndex: number | undefined;
        propertyIndex: number | undefined;
      }[]
    | null
  >(null);

  const [hasUndefinedCascader, setHasUndefinedCascader] = useState(true);
  const [isSubmittingGroupBy, setSubmittingGroupBy] = useState(false);
  const [isSubmittingUngroup, setSubmittingUngroup] = useState(false);
  const [ungroupIndex, setUngroupIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUpdatedTable, setHasUpdatedTable] = useState(false);

  const propertiesByCategory: CascaderData[] = useMemo(
    () =>
      propertySets.map((set) => {
        if (set.category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
          return {
            label: t('quantities'),
            children: set.properties.map((property) => {
              return { label: property };
            }),
          };
        } else if (set.category === SPARKEL_LOCATION_PROPERTY_SET) {
          return {
            label: t('location'),
            children: set.properties.map((property) => {
              return { label: property };
            }),
          };
        } else {
          return {
            label: set.category,
            children: set.properties.map((property) => {
              return { label: property };
            }),
          };
        }
      }),
    [propertySets, t]
  );

  useEffect(() => {
    if (isOpen) {
      if (isUpdatingTable) {
        setIsLoading(true);
      } else {
        setHasUpdatedTable(true);
      }
    }
  }, [isOpen, isUpdatingTable]);

  useEffect(() => {
    if (!isSubmittingGroupBy && !isSubmittingUngroup && hasUpdatedTable) {
      setUngroupIndex(-1);
      setIsLoading(false);
      setHasUpdatedTable(false);
    }
  }, [isSubmittingGroupBy, isSubmittingUngroup, hasUpdatedTable]);

  useEffect(() => {
    if (isOpen && defaultAttributes && defaultAttributes.length > 0) {
      const indexPairs = defaultAttributes
        .map((atrr) => {
          let category = atrr.category;
          if (atrr.category === SPARKEL_LOCATION_PROPERTY_SET) {
            category = t('location');
          } else if (atrr.category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
            category = t('quantities');
          }
          return { category, name: atrr.name };
        })
        .map((attr) => {
          // Find the property set index (using the last index if multiple)
          const propertySetIndex = propertiesByCategory.reduce(
            (lastIndex, pSet, currentIndex) =>
              pSet.label === attr.category ? currentIndex : lastIndex,
            -1
          );

          // If property set index is not found, return null
          if (propertySetIndex === -1)
            return { propertySetIndex: undefined, propertyIndex: undefined };

          // Find the property index in the property set
          const propertyIndex = propertiesByCategory[
            propertySetIndex
          ].children?.findIndex((property) => property.label === attr.name);

          // If property index is not found, return null
          if (propertyIndex === undefined || propertyIndex === -1)
            return { propertySetIndex: undefined, propertyIndex: undefined };

          // Return the index pair
          return { propertySetIndex, propertyIndex };
        });

      // Check if we have valid pairs for all attributes
      if (indexPairs.length !== defaultAttributes.length) {
        // TODO: Proper error handling
        return;
      }

      setGroupingProperties(indexPairs);
      setHasUndefinedCascader(false);
    } else {
      setGroupingProperties(null);
      setHasUndefinedCascader(true);
    }
  }, [defaultAttributes, propertiesByCategory, isOpen, t]);

  const activeIconColor = useColorModeValue('teal.500', 'teal.200');

  const propertyByCategoryWithFriendlyNames = propertiesByCategory.map(
    (property) => {
      let newProp = { ...property };
      newProp.children = property.children?.map((prop) => {
        return {
          label: displayNameFromProperty(prop.label, t),
          children: prop.children,
        };
      });
      return newProp;
    }
  );

  return (
    <PopoverContent shadow={'lg'} width={240}>
      <PopoverBody>
        <Heading as={'h3'} size={'xs'} marginY={1}>
          {t('table:breakdown.title')}
        </Heading>
        <VStack>
          {groupingProperties
            ? groupingProperties.map((indexPair, idx) => {
                if (
                  indexPair.propertySetIndex === undefined ||
                  indexPair.propertyIndex === undefined
                )
                  return;

                const category =
                  propertySets[indexPair.propertySetIndex].category;
                const propertyName =
                  propertySets[indexPair.propertySetIndex].properties[
                    indexPair.propertyIndex
                  ];

                let placeholder = '';
                if (category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
                  placeholder = `${t('quantities')} / ${displayNameFromProperty(
                    propertyName,
                    t
                  )}`;
                } else if (category === SPARKEL_LOCATION_PROPERTY_SET) {
                  placeholder = `${t('location')} / ${displayNameFromProperty(
                    propertyName,
                    t
                  )}`;
                } else {
                  placeholder = `${category} / ${propertyName}`;
                }

                return (
                  <HStack key={idx} align={'end'} width={'100%'}>
                    <Box flex={1}>
                      <PropertyCascader
                        aria-label={`property cascader ${idx}`}
                        data={propertyByCategoryWithFriendlyNames}
                        size="xs"
                        variant={'filled'}
                        closeOnSelect={false}
                        marginTop={2}
                        isLoading={isLoading}
                        defaultSelection={[
                          indexPair.propertySetIndex,
                          indexPair.propertyIndex,
                        ]}
                        _placeholder={{
                          color: activeIconColor,
                        }}
                        placeholder={placeholder}
                        onCascaderClose={() => {
                          if (onUsingCascader && isOpen) onUsingCascader(false);
                        }}
                        onCascaderOpen={() => {
                          if (onUsingCascader && isOpen) onUsingCascader(true);
                        }}
                        onPropertySelect={(selectedIndices) => {
                          onSubmitInternal(selectedIndices, idx);
                        }}
                      />
                    </Box>
                    <Tooltip label={t('table:breakdown.remove.tooltip')}>
                      <IconButton
                        size={'xs'}
                        colorScheme="gray"
                        variant={'ghost'}
                        aria-label="remove group"
                        icon={<SparkelIcon icon={faTimes} />}
                        isDisabled={ungroupIndex !== -1 || isUpdatingTable}
                        isLoading={ungroupIndex === idx}
                        onClick={() => onUngroupInternal(idx)}
                      />
                    </Tooltip>
                  </HStack>
                );
              })
            : undefined}
        </VStack>
        {hasUndefinedCascader ? (
          <HStack align={'end'} width={'100%'} marginTop={2}>
            <Box flex={1}>
              <PropertyCascader
                aria-label="unassigned property cascader"
                data={propertyByCategoryWithFriendlyNames}
                size="xs"
                variant={'filled'}
                closeOnSelect={false}
                marginTop={2}
                isLoading={isLoading}
                placeholder={t('table:breakdown.assign.placeholder')}
                onCascaderClose={() => {
                  if (onUsingCascader && isOpen) onUsingCascader(false);
                }}
                onCascaderOpen={() => {
                  if (onUsingCascader && isOpen) onUsingCascader(true);
                }}
                onPropertySelect={(selectedIndices) => {
                  onSubmitInternal(selectedIndices);
                  // After property is selected, this undefined cascader will be removed so no cascader will be used.
                  if (onUsingCascader) onUsingCascader(false);
                }}
              />
            </Box>
            {groupingProperties && groupingProperties.length > 0 ? (
              <Tooltip label={t('table:breakdown.remove.tooltip')}>
                <IconButton
                  size={'xs'}
                  colorScheme="gray"
                  variant={'ghost'}
                  aria-label="remove group"
                  icon={<SparkelIcon icon={faTimes} />}
                  isDisabled={ungroupIndex !== -1 || isUpdatingTable}
                  onClick={() => setHasUndefinedCascader(false)}
                />
              </Tooltip>
            ) : undefined}
          </HStack>
        ) : (
          <Button
            marginTop={2}
            leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
            size={'xs'}
            variant={'ghost'}
            isDisabled={
              isSubmittingGroupBy || ungroupIndex !== -1 || isUpdatingTable
            }
            onClick={() => {
              if (groupingProperties) {
                setHasUndefinedCascader(true);
              }
            }}
          >
            {t('table:breakdown.then-by')}
          </Button>
        )}
      </PopoverBody>
    </PopoverContent>
  );
}
